import type { Hookable } from 'hookable'

import type { DocumentContextEvents } from '../useDocumentContext.events.schema'

type DocumentContextHookMap = {
  [K in keyof DocumentContextEvents]: (event: DocumentContextEvents[K]) => void | Promise<void>
}

/**
 * Handle for removing a previously registered document context hook.
 */
type DocumentContextUnsubscribe = () => void

interface DocumentContextLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

interface UseDocumentContextOnDocumentIdChangedParams {
  hooks: Hookable<DocumentContextHookMap>
  logger: DocumentContextLoggerLike
  clearDocumentScopedQueries: () => void
}

/**
 * Internal handler: clears document-scoped queries when the active doc id is removed.
 */
export default function useDocumentContextOnDocumentIdChanged({
  hooks,
  logger,
  clearDocumentScopedQueries,
}: UseDocumentContextOnDocumentIdChangedParams) {
  const unsubscribe: DocumentContextUnsubscribe = hooks.hook(
    'DocumentIdChanged',
    ({ documentId, previousDocumentId, path }) => {
      logger.debug('DocumentId changed, syncing document context', {
        path,
        previousDocumentId,
        documentId,
      })

      if (documentId) return

      clearDocumentScopedQueries()
    },
  )

  return unsubscribe
}
